import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useContact } from "@/contexts/ContactContext";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Showcase", href: "#showcase" },
  { label: "Technology", href: "#technology" },
  { label: "How We Work", href: "#how-we-work" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openContact } = useContact();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 overflow-visible transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16 flex items-center justify-between h-16 md:h-20 overflow-visible">
        {/* Left: oversized logo — top-aligned so it hangs below, never clipped above */}
        <a
          href="#home"
          aria-label="1902 Enterprises"
          data-testid="nav-logo"
          className="relative z-20 shrink-0 self-start pt-2 group"
        >
          <span className="inline-flex items-center justify-center bg-black p-2 sm:p-2.5 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
            <img
              src="/images/logo.png"
              alt="1902 Enterprises"
              className="h-28 sm:h-32 md:h-40 w-auto object-contain drop-shadow-[0_8px_28px_rgba(146,24,136,0.45)] transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </span>
        </a>

        {/* Desktop nav + CTA */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 z-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors relative group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-fuchsia-500 group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <button
            type="button"
            onClick={() => openContact({ source: "navbar" })}
            data-testid="nav-cta-get-started"
            className="inline-flex items-center gap-2 bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 ember-glow ml-2"
          >
            Get Started <ArrowUpRight className="w-4 h-4" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-white z-10"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-2xl border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium text-white/80 hover:text-fuchsia-400"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openContact({ source: "navbar-mobile" });
              }}
              data-testid="nav-mobile-cta"
              className="inline-flex items-center justify-center gap-2 bg-fuchsia-500 text-white text-sm font-semibold px-5 py-3 rounded-full mt-2"
            >
              Get Started <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
